import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import Layout from '../components/layout/Layout';
import { useSiteContent } from '../context/SiteContentContext';
import { toast } from 'sonner';
import {
  Phone,
  Mail,
  MapPin,
  Clock,
  Send,
  MessageCircle,
  Building2,
  User,
  AtSign,
  FileText
} from 'lucide-react';

const ContatoPage = () => {
  const { siteContent } = useSiteContent();
  const companyInfo = siteContent.companyInfo;
  const contactPage = siteContent.contactPage || {};
  const hero = contactPage.hero || {};
  const form = contactPage.form || {};
  const formFields = form.fields || {};
  const whatsappCta = contactPage.whatsappCta || {};
  const hours = contactPage.hours || {};
  const coverage = contactPage.coverage || {};
  const contactCards = Array.isArray(contactPage.cards)
    ? contactPage.cards
    : Array.isArray(contactPage.cards?.items)
    ? contactPage.cards.items
    : [];
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleWhatsApp = () => {
    window.open(
      `https://wa.me/${companyInfo.whatsapp}?text=${encodeURIComponent(companyInfo.whatsappMessage)}`,
      '_blank'
    );
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    // Simulate form submission
    await new Promise(resolve => setTimeout(resolve, 1500));
    
    toast.success('Mensagem enviada com sucesso! Entraremos em contato em breve.');
    setFormData({
      name: '',
      email: '',
      company: '',
      phone: '',
      subject: '',
      message: ''
    });
    setIsSubmitting(false);
  };

  const iconMap = {
    Phone: Phone,
    Mail: Mail,
    MapPin: MapPin,
    MessageCircle: MessageCircle
  };

  return (
    <Layout>
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900" />
        <div className="absolute top-1/3 left-10 w-72 h-72 bg-orange-500/20 rounded-full blur-3xl" />
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <span className="inline-block px-4 py-2 rounded-full bg-white/10 text-white/80 text-sm font-medium mb-6">
              {hero.badge || 'Contato'}
            </span>
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
              {hero.title || 'Fale com a'} <span className="text-orange-500">{hero.highlight || 'DRSUPORTI'}</span>
            </h1>
            <p className="text-xl text-slate-300 leading-relaxed">
              {hero.description || 'Estamos prontos para atender sua empresa. Entre em contato e descubra como podemos ajudar.'}
            </p>
          </div>
        </div>
      </section>

      {/* Contact Info Cards */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {contactCards.map((info, index) => {
              const IconComponent = iconMap[info?.icon];
              return (
                <Card
                  key={`${info?.title || 'card'}-${index}`}
                  className="border-0 shadow-lg shadow-slate-200/50 hover:shadow-xl hover:shadow-orange-100 transition-all duration-300"
                >
                  <CardContent className="p-6 text-center">
                    <div className="w-14 h-14 mx-auto rounded-xl bg-gradient-to-br from-orange-500 to-orange-600 flex items-center justify-center mb-4">
                      {IconComponent ? <IconComponent className="w-7 h-7 text-white" /> : null}
                    </div>
                    <h3 className="font-bold text-slate-800 mb-1">{info.title}</h3>
                    <p className="text-sm text-slate-500 mb-2">{info.description}</p>
                    {info.link ? (
                      <a
                        href={info.link}
                        target={info.icon === 'MessageCircle' ? '_blank' : undefined}
                        rel={info.icon === 'MessageCircle' ? 'noopener noreferrer' : undefined}
                        className="text-orange-500 font-medium hover:text-orange-600 transition-colors"
                      >
                        {info.value}
                      </a>
                    ) : (
                      <span className="text-orange-500 font-medium">{info.value}</span>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Contact Form Section */}
      <section className="py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16">
            {/* Form */}
            <div>
              <span className="inline-block px-4 py-2 rounded-full bg-orange-100 text-orange-600 text-sm font-medium mb-4">
                {form.badge || 'Formulário de Contato'}
              </span>
              <h2 className="text-3xl md:text-4xl font-bold text-slate-800 mb-4">
                {form.title || 'Envie sua mensagem'}
              </h2>
              <p className="text-slate-600 mb-8">
                {form.description || 'Preencha o formulário abaixo e nossa equipe entrará em contato em até 24 horas úteis.'}
              </p>

              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid sm:grid-cols-2 gap-6">
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
                    <Input
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      placeholder={formFields.name || 'Seu nome'}
                      required
                      className="pl-10 h-12 border-slate-200"
                    />
                  </div>
                  <div className="relative">
                    <AtSign className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
                    <Input
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder={formFields.email || 'Seu e-mail'}
                      required
                      className="pl-10 h-12 border-slate-200"
                    />
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-6">
                  <div className="relative">
                    <Building2 className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
                    <Input
                      name="company"
                      value={formData.company}
                      onChange={handleChange}
                      placeholder={formFields.company || 'Nome da empresa'}
                      className="pl-10 h-12 border-slate-200"
                    />
                  </div>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
                    <Input
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder={formFields.phone || 'Telefone'}
                      className="pl-10 h-12 border-slate-200"
                    />
                  </div>
                </div>

                <div className="relative">
                  <FileText className="absolute left-3 top-3 w-5 h-5 text-slate-400" />
                  <Input
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    placeholder={formFields.subject || 'Assunto'}
                    required
                    className="pl-10 h-12 border-slate-200"
                  />
                </div>

                <Textarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  placeholder={formFields.message || 'Sua mensagem'}
                  required
                  rows={6}
                  className="border-slate-200 resize-none"
                />

                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-orange-500 hover:bg-orange-600 text-white font-semibold h-12 text-lg shadow-lg shadow-orange-500/25 disabled:opacity-70"
                >
                  {isSubmitting ? (
                    form.sendingLabel || 'Enviando...'
                  ) : (
                    <>
                      <Send className="w-5 h-5 mr-2" />
                      {form.submitLabel || 'Enviar mensagem'}
                    </>
                  )}
                </Button>
              </form>
            </div>

            {/* WhatsApp CTA */}
            <div className="lg:pl-8">
              <div className="sticky top-32">
                <div className="p-8 rounded-2xl bg-gradient-to-br from-slate-900 to-slate-800 text-white">
                  <div className="w-16 h-16 rounded-2xl bg-green-500 flex items-center justify-center mb-6">
                    <MessageCircle className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-2xl font-bold mb-4">
                    {whatsappCta.title || 'Prefere atendimento imediato?'}
                  </h3>
                  <p className="text-slate-300 mb-6 leading-relaxed">
                    {whatsappCta.description || 'Fale diretamente com um de nossos especialistas pelo WhatsApp e receba atendimento personalizado em tempo real.'}
                  </p>
                  <Button
                    onClick={handleWhatsApp}
                    className="w-full bg-green-500 hover:bg-green-600 text-white font-semibold h-14 text-lg shadow-xl shadow-green-500/25"
                  >
                    <MessageCircle className="w-5 h-5 mr-2" />
                    {whatsappCta.buttonLabel || 'Fale com um especialista'}
                  </Button>

                  <div className="mt-8 pt-8 border-t border-white/10">
                    <h4 className="font-semibold mb-4 flex items-center gap-2">
                      <Clock className="w-5 h-5 text-orange-400" />
                      {hours.title || 'Horário de atendimento'}
                    </h4>
                    <div className="space-y-2 text-sm text-slate-300">
                      {(hours.lines || []).map((line, index) => (
                        <p key={`${line}-${index}`}>{line}</p>
                      ))}
                      {hours.highlight ? (
                        <p className="text-orange-400 font-medium">{hours.highlight}</p>
                      ) : null}
                    </div>
                  </div>
                </div>

                {/* Coverage Map Placeholder */}
                <div className="mt-8 p-6 rounded-2xl bg-white shadow-lg shadow-slate-200/50">
                  <h4 className="font-bold text-slate-800 mb-4 flex items-center gap-2">
                    <MapPin className="w-5 h-5 text-orange-500" />
                    {coverage.title || 'Área de Atuação'}
                  </h4>
                  <p className="text-slate-600 text-sm mb-4">
                    {coverage.description || 'Atendemos empresas nos estados do Maranhão, Piauí e São Paulo, com suporte presencial e remoto.'}
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {(coverage.states || []).map((state) => (
                      <span
                        key={state}
                        className="px-4 py-2 rounded-full bg-orange-100 text-orange-600 text-sm font-medium"
                      >
                        {state}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default ContatoPage;
